/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

/* eslint-env browser */

import EventEmitter from 'eventemitter3';
import JavascriptStateMachine from 'javascript-state-machine';
import isNil from 'lodash/isNil';

import ErrorLevel from '../../error/error-level';
import StateMachineFactory from '../../state-machine/state-machine-factory';
import StateMachine from '../../state-machine/state-machine';
import ViewerLifeCycle from '../life-cycle/viewer-life-cycle';

// TODO Zabezpečení:
// - Kvůli problémům s funkčností web aplikací je u sandbox povoleno "allow-same-origin".
// - To je bezpečnostním rizikem, obzvláště při spuštění aplikace ze stejného origin.
//   - Může např. zavolat `localstorage.clear()` a tím v podstatě odinstalovat lighter zařízení.
// - Ošetření do budoucna:
//   - Přidat na serveru přepínač "vypnout zabezpečení", který by toto udělal jen pro vybrané důvěryhodné weby.
//   - Přes message channel poskytovat speciální lighter rozhraní pro práci s localstorage a případné další služby.
//   - Možná zkusit zakázat URL začínající `file://` a zabránit shodě origin.
//     - Ale je otázkou jestli to nejde obejít (aliasy apod.).
/**
 * Ovladač pro zobrazování webových stránek.
 * @implements {ViewerController}
 */
class ViewerControllerIframe {
  /**
   * Vytvoří instanci.
   * @param {number} loadDelay
   * @param {HtmlIframePreflighter} preflighter
   * @param {Object} [opts={}]
   * @param {boolean} [opts.scroll] Umožnit scroll v iframe.
   * @returns {ViewerControllerIframe} Nová instance.
   */
  constructor(loadDelay, preflighter, { scroll = false } = {}) {
    /**
     * Správce událostí.
     * @private
     * @type {EventEmitter}
     */
    this.emitter = new EventEmitter();
    this.loadDelay = loadDelay;
    /**
     * Timeout ID pro odpočet do maximální doby čekání na načtení iframe.
     * Dokumentace:
     * [Stack Overflow](https://stackoverflow.com/questions/375710/detect-failure-to-load-contents-of-an-iframe).
     * @private
     * @type {?number}
     */
    this.loadTimeout = null;
    this.preflighter = preflighter;
    this.scheduleInfo = null;
    this.scroll = scroll;
    const stateMachineFactory = new StateMachineFactory(
      JavascriptStateMachine,
      StateMachine
    );
    this.viewerLifeCycle = new ViewerLifeCycle(stateMachineFactory);
    this.viewerSource = null;
    // Navázání naslouchání na instanci.
    this.onElementError = this.onElementError.bind(this);
    this.onElementLoad = this.onElementLoad.bind(this);
    this.onLoadTimeout = this.onLoadTimeout.bind(this);
  }

  /**
   * Naváže naslouchání událostem z elementu.
   * @param {HTMLIframeElement} el Element, kterému se naslouchá.
   * @private
   * @returns {undefined} Nic.
   */
  bindElement(el) {
    el.addEventListener('error', this.onElementError);
    el.addEventListener('load', this.onElementLoad);
  }

  /** @inheritdoc */
  destroy() {
    const { viewerSource } = this;
    if (!isNil(viewerSource)) {
      const el = viewerSource.getEl();
      this.unbindElement(el);
    }
  }

  /** @inheritdoc */
  enter() {
    return this.viewerLifeCycle.enter(() => {
      // Zobrazení obalujícího elementu.
      const viewerFrame = this.viewerSource.getFrame();
      viewerFrame.enter();
    });
  }

  /** @inheritdoc */
  leave() {
    return this.viewerLifeCycle.leave(() => {
      // Skrytí obalujícího elementu.
      const viewerFrame = this.viewerSource.getFrame();
      viewerFrame.leave();
    });
  }

  /** @inheritdoc */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @inheritdoc */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * Reakce na chybu v elementu.
   * @private
   * @returns {undefined} Nic.
   */
  onElementError() {
    clearTimeout(this.loadTimeout);
    const error = new Error('Unknown HTMLIframeElement error.');
    this.emitter.emit('error', error);
  }

  /**
   * Reakce na načtení zdroje v elementu.
   * @private
   * @returns {undefined} Nic.
   */
  onElementLoad() {
    clearTimeout(this.loadTimeout);
    this.emitter.emit('loaded');
  }

  /**
   * Reakce na vypršení časovače pro load.
   * @private
   * @returns {undefined}
   */
  onLoadTimeout() {
    const error = new Error('HTMLIframeElement load timed out.');
    this.emitter.emit('error', error);
  }

  /**
   * Reakce na chybu při testování dostupnosti URL pro iframe.
   * @param {Error} error
   * @private
   * @returns {undefined}
   */
  onPreflightError(error) {
    // Snížení priority logování pro chyby síťového spojení.
    error.level = ErrorLevel.Warning;
    this.emitter.emit('error', error);
  }

  /** @inheritdoc */
  release() {
    return this.viewerLifeCycle.release(() => {
      const { viewerSource } = this;
      // Uvolnění obalujícího elementu.
      const viewerFrame = viewerSource.getFrame();
      viewerFrame.release();
      // Uvolnění zdrojového elementu.
      const el = viewerSource.getEl();
      this.unbindElement(el);
      // Časovač pro načtení.
      clearTimeout(this.loadTimeout);
      // Zrušení hodnot v instanci.
      this.scheduleInfo = null;
      this.viewerSource = null;
    });
  }

  /** @inheritdoc */
  seize(scheduleInfo, viewerSource) {
    return this.viewerLifeCycle.seize(() => {
      // Nastavení hodnot v instanci.
      this.scheduleInfo = scheduleInfo;
      this.viewerSource = viewerSource;
      // Určení všech potřebných hodnot.
      return scheduleInfo.getUrl().then((url) => {
        const viewerFrame = viewerSource.getFrame();
        const el = viewerSource.getEl();
        // Příprava obalujícího elementu.
        viewerFrame.seize(scheduleInfo);
        // Příprava zdrojového elementu.
        this.bindElement(el);
        // Nastavení oprávnění pro přístup k vybraným funkcionalitám.
        el.allow = 'autoplay;camera;geolocation;microphone';
        el.classList.add('viewer-controller-iframe');
        el.sandbox = 'allow-forms allow-same-origin allow-scripts';
        // Deaktivace scrollování v iframe.
        if (!this.scroll) {
          el.classList.add('viewer-controller-iframe--no-scroll');
          // Označeno jako deprecated, ale některé prohlížeče ho vyžadují.
          el.scrolling = 'no';
        }
        // Otestuje se, jestli je URL dostupná.
        // Přes přiřazení do iframe to nelze zjistit.
        // Na výsledek dotazu se nečeká, stejně jako na nastavení zdroje.
        this.preflighter
          .preflight(url)
          .catch((error) => this.onPreflightError(error));
        // Časovač pro načtení.
        this.loadTimeout = setTimeout(this.onLoadTimeout, this.loadDelay);
        // Nastavení zdroje.
        el.src = url;
      });
    });
  }

  /**
   * Uvolní naslouchání událostem z elementu.
   * @param {HTMLIframeElement} el Element, kterému se naslouchá.
   * @private
   * @returns {undefined} Nic.
   */
  unbindElement(el) {
    el.removeEventListener('error', this.onElementError);
    el.removeEventListener('load', this.onElementLoad);
  }
}

export default ViewerControllerIframe;
